import React, { useState, useMemo } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    Box,
    Typography
} from '@mui/material';
import { Eye, Mail, Phone, MapPin, Calendar, Smartphone, Monitor } from 'lucide-react';
import { format } from 'date-fns';

const LeadTable = ({ leads = [], onLeadClick, activeFilter = null }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [orderBy, setOrderBy] = useState('date_issued');
    const [order, setOrder] = useState('desc');

    // Filter leads based on activeFilter
    const filteredLeads = useMemo(() => {
        if (!activeFilter) return leads;

        return leads.filter(lead => {
            switch (activeFilter) {
                case 'onsite_bookings':
                    return lead.status === 'Onsite Quotes';
                case 'online_bookings':
                    return lead.status === 'Online Quotes';
                case 'price_estimates':
                    return lead.status === 'Price Estimates';
                case 'price_estimate_conversions':
                    return lead.status === 'Price Estimates' && lead.simpro_quote;
                case 'dropoffs':
                    return lead.status === 'Drop Off';
                case 'iphone_views':
                    return lead.Device && lead.Device.toLowerCase().includes('iphone');
                case 'android_views':
                    return lead.Device && lead.Device.toLowerCase().includes('android');
                case 'pc_views':
                    return lead.Device && !lead.Device.toLowerCase().includes('mobile') &&
                        !lead.Device.toLowerCase().includes('iphone') &&
                        !lead.Device.toLowerCase().includes('android');
                case 'page_views':
                    return lead.page_view_count > 0;
                case 'total_leads':
                    return true; // Show all leads
                default:
                    return true;
            }
        });
    }, [leads, activeFilter]);

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setPage(0); // Reset to first page when sorting
    };

    const sortedLeads = useMemo(() => {
        return [...filteredLeads].sort((a, b) => {
            let aValue, bValue;

            switch (orderBy) {
                case 'customer':
                    aValue = `${a.Customer_name || ''} ${a.Customer_surname || ''}`.trim().toLowerCase();
                    bValue = `${b.Customer_name || ''} ${b.Customer_surname || ''}`.trim().toLowerCase();
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                case 'date_issued':
                    aValue = new Date(a.date_issued || 0);
                    bValue = new Date(b.date_issued || 0);
                    break;
                case 'page_views':
                    aValue = a.page_view_count || 0;
                    bValue = b.page_view_count || 0;
                    break;
                case 'product':
                    aValue = (a.product || a.service_type || '').toLowerCase();
                    bValue = (b.product || b.service_type || '').toLowerCase();
                    break;
                case 'location':
                    aValue = [a.suburb, a.postcode].filter(Boolean).join(', ').toLowerCase();
                    bValue = [b.suburb, b.postcode].filter(Boolean).join(', ').toLowerCase();
                    break;
                default:
                    aValue = a[orderBy] || '';
                    bValue = b[orderBy] || '';
            }

            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [filteredLeads, orderBy, order]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Online Quotes':
                return { backgroundColor: '#1976d2', color: 'white' };
            case 'Onsite Quotes':
                return { backgroundColor: '#388e3c', color: 'white' };
            case 'Price Estimates':
                return { backgroundColor: '#f57c00', color: 'white' };
            case 'Drop Off':
                return { backgroundColor: '#d32f2f', color: 'white' };
            default:
                return { backgroundColor: '#757575', color: 'white' };
        }
    };

    const getDeviceIcon = (deviceString) => {
        if (!deviceString) return Monitor;

        const device = deviceString.toLowerCase();
        if (device.includes('iphone') || device.includes('ipad') || device.includes('mobile') || device.includes('android')) {
            return Smartphone;
        }
        return Monitor;
    };

    const paginatedLeads = sortedLeads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden', bgcolor: '#1a1a1a', border: '1px solid #333' }}>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                Lead ID
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                <TableSortLabel
                                    active={orderBy === 'customer'}
                                    direction={orderBy === 'customer' ? order : 'asc'}
                                    onClick={() => handleSort('customer')}
                                    sx={{
                                        color: '#e0e0e0 !important',
                                        '&:hover': { color: '#4fc3f7 !important' },
                                        '&.Mui-active': { color: '#4fc3f7 !important' },
                                        '& .MuiTableSortLabel-icon': { color: '#4fc3f7 !important' }
                                    }}
                                >
                                    Customer
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                Contact
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                <TableSortLabel
                                    active={orderBy === 'product'}
                                    direction={orderBy === 'product' ? order : 'asc'}
                                    onClick={() => handleSort('product')}
                                    sx={{
                                        color: '#e0e0e0 !important',
                                        '&:hover': { color: '#4fc3f7 !important' },
                                        '&.Mui-active': { color: '#4fc3f7 !important' },
                                        '& .MuiTableSortLabel-icon': { color: '#4fc3f7 !important' }
                                    }}
                                >
                                    Product/Service
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                <TableSortLabel
                                    active={orderBy === 'status'}
                                    direction={orderBy === 'status' ? order : 'asc'}
                                    onClick={() => handleSort('status')}
                                    sx={{
                                        color: '#e0e0e0 !important',
                                        '&:hover': { color: '#4fc3f7 !important' },
                                        '&.Mui-active': { color: '#4fc3f7 !important' },
                                        '& .MuiTableSortLabel-icon': { color: '#4fc3f7 !important' }
                                    }}
                                >
                                    Status
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                <TableSortLabel
                                    active={orderBy === 'date_issued'}
                                    direction={orderBy === 'date_issued' ? order : 'asc'}
                                    onClick={() => handleSort('date_issued')}
                                    sx={{
                                        color: '#e0e0e0 !important',
                                        '&:hover': { color: '#4fc3f7 !important' },
                                        '&.Mui-active': { color: '#4fc3f7 !important' },
                                        '& .MuiTableSortLabel-icon': { color: '#4fc3f7 !important' }
                                    }}
                                >
                                    Created
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                <TableSortLabel
                                    active={orderBy === 'location'}
                                    direction={orderBy === 'location' ? order : 'asc'}
                                    onClick={() => handleSort('location')}
                                    sx={{
                                        color: '#e0e0e0 !important',
                                        '&:hover': { color: '#4fc3f7 !important' },
                                        '&.Mui-active': { color: '#4fc3f7 !important' },
                                        '& .MuiTableSortLabel-icon': { color: '#4fc3f7 !important' }
                                    }}
                                >
                                    Location
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                Device
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                <TableSortLabel
                                    active={orderBy === 'page_views'}
                                    direction={orderBy === 'page_views' ? order : 'asc'}
                                    onClick={() => handleSort('page_views')}
                                    sx={{
                                        color: '#e0e0e0 !important',
                                        '&:hover': { color: '#4fc3f7 !important' },
                                        '&.Mui-active': { color: '#4fc3f7 !important' },
                                        '& .MuiTableSortLabel-icon': { color: '#4fc3f7 !important' }
                                    }}
                                >
                                    Page Views
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sx={{ bgcolor: '#2d2d2d', color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedLeads.map((lead) => {
                            const DeviceIcon = getDeviceIcon(lead.Device);
                            const fullName = `${lead.Customer_name || ''} ${lead.Customer_surname || ''}`.trim() || 'Unknown';
                            const location = [lead.suburb, lead.postcode].filter(Boolean).join(', ') || 'Not provided';

                            return (
                                <TableRow
                                    key={lead.id}
                                    hover
                                    sx={{
                                        '&:hover': { bgcolor: '#2a2a2a' },
                                        bgcolor: '#1a1a1a',
                                        borderBottom: '1px solid #333'
                                    }}
                                >
                                    <TableCell sx={{ color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4fc3f7' }}>
                                            L{lead.id}
                                        </Typography>
                                        {lead.simpro_quote && (
                                            <Typography variant="caption" sx={{ color: '#81c784', display: 'block' }}>
                                                Quote: {lead.simpro_quote}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell sx={{ color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                            {fullName}
                                        </Typography>
                                        {lead.isHighValue && (
                                            <Chip
                                                label="High Value"
                                                size="small"
                                                sx={{
                                                    mt: 0.5,
                                                    bgcolor: '#f57c00',
                                                    color: 'white',
                                                    fontSize: '0.7rem'
                                                }}
                                            />
                                        )}
                                    </TableCell>

                                    <TableCell sx={{ color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {lead.email && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Mail size={14} style={{ color: '#4fc3f7' }} />
                                                    <Typography variant="caption" sx={{ color: '#4fc3f7' }}>
                                                        {lead.email}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {lead.phone && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Phone size={14} style={{ color: '#81c784' }} />
                                                    <Typography variant="caption" sx={{ color: '#81c784' }}>
                                                        {lead.phone}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={{ color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                        <Typography variant="body2">
                                            {lead.product || lead.service_type || 'Not specified'}
                                        </Typography>
                                        {lead.service_type && (
                                            <Typography variant="caption" sx={{ color: '#ba68c8', display: 'block' }}>
                                                {lead.service_type}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell sx={{ borderBottom: '1px solid #333' }}>
                                        <Chip
                                            label={lead.status}
                                            size="small"
                                            sx={getStatusColor(lead.status)}
                                        />
                                    </TableCell>

                                    <TableCell sx={{ color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Calendar size={14} style={{ color: '#ffb74d' }} />
                                            <Typography variant="caption">
                                                {lead.date_issued ? format(new Date(lead.date_issued), 'MMM d, yyyy') : 'N/A'}
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block' }}>
                                            {lead.daysInStatus} days ago
                                        </Typography>
                                    </TableCell>

                                    <TableCell sx={{ color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <MapPin size={14} style={{ color: '#ffb74d' }} />
                                            <Typography variant="caption">
                                                {location}
                                            </Typography>
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={{ color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <DeviceIcon size={14} style={{ color: '#ba68c8' }} />
                                            <Typography variant="caption">
                                                {lead.Device ? (
                                                    lead.Device.includes('Mobile') || lead.Device.includes('iPhone') || lead.Device.includes('Android')
                                                        ? 'Mobile'
                                                        : 'Desktop'
                                                ) : 'Unknown'}
                                            </Typography>
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={{ color: '#e0e0e0', borderBottom: '1px solid #333' }}>
                                        <Typography variant="body2" sx={{ color: '#4fc3f7', fontWeight: 'bold' }}>
                                            {lead.page_view_count || 0}
                                        </Typography>
                                        {lead.last_page_view && (
                                            <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block' }}>
                                                Last: {format(new Date(lead.last_page_view), 'MMM d')}
                                            </Typography>
                                        )}
                                    </TableCell>

                                    <TableCell sx={{ borderBottom: '1px solid #333' }}>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                onClick={() => onLeadClick(lead)}
                                                sx={{
                                                    color: '#4fc3f7',
                                                    '&:hover': { bgcolor: '#2a2a2a' }
                                                }}
                                            >
                                                <Eye size={16} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={sortedLeads.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                    bgcolor: '#2d2d2d',
                    color: '#e0e0e0',
                    borderTop: '1px solid #333',
                    '& .MuiTablePagination-selectLabel': { color: '#e0e0e0' },
                    '& .MuiTablePagination-displayedRows': { color: '#e0e0e0' },
                    '& .MuiTablePagination-select': { color: '#e0e0e0' },
                    '& .MuiSvgIcon-root': { color: '#e0e0e0' }
                }}
            />
        </Paper>
    );
};

export default LeadTable;
